/**
 * @file order-header-meta.tsx
 * @description Плитки шапки карточки заказа
 * (phase19.24-order-card-chain-design.md §4, §5; эталон —
 * `components/wms/picking/PickingTaskMeta.tsx`).
 *
 * Заказ показывает те же плитки, что складской документ: читатель любого
 * документа админки находит «кто / когда / итоги» на одном месте. Первая
 * строка отвечает на вопросы «кто купил, когда, оплачено ли, на сколько»,
 * вторая — «откуда пришёл, чем везём, кто ведёт, когда меняли».
 *
 * Плитки «Клиент», «Оплата» и «Доставка» — ссылки (§4): по ним уходят в
 * карточку клиента и во вкладки, а не ищут их глазами среди вкладок.
 *
 * phase19.31 (SC43): подписи по-русски («Ответственный», «Сумма»), значения
 * обычным шрифтом с табличными цифрами, деньги — «175,00 ₽».
 */

import { Link } from "react-router";

import {
  ORDER_SOURCE_LABEL,
  currencySymbol,
  formatMoney,
  type OrderDto,
} from "../../lib/api-orders";
import { orderTabHref } from "../../lib/order-chain-links";
import { buildDeliveryLine, buildPaymentLine } from "../../lib/order-summary-lines";
import type { DocumentMetaItem } from "../ui/DocumentMetaGrid";
import { formatDocDateTime } from "../wms/wms-document-meta";

interface MetaInput {
  order: OrderDto;
  slug: string | undefined;
  /** Право `SHOP_VIEW_CUSTOMERS`: без него клиент — просто текст. */
  canOpenCustomer: boolean;
}

/**
 * Классы ссылки-значения плитки: одна форма на все плитки шапки.
 *
 * На `<lg` ссылка — тач-таргет ≥48px (`inline-flex min-h-12`): QA намерил у
 * «Клиента» 17px, у «Оплаты» 37px — пальцем в такую не попасть. На `lg`
 * высота возвращается к тексту, иначе ряд плиток становится выше эталона
 * складского документа.
 */
const LINK =
  "inline-flex min-h-12 items-center text-primary underline underline-offset-2 lg:min-h-0";

export function buildOrderHeaderMeta({
  order,
  slug,
  canOpenCustomer,
}: MetaInput): DocumentMetaItem[] {
  const customerName = order.customerDisplayName ?? "—";
  const payment = buildPaymentLine(order);
  const delivery = buildDeliveryLine(order);
  const ctx = { slug, orderId: order.id };

  return [
    {
      label: "Клиент",
      value:
        canOpenCustomer && slug ? (
          <Link to={`/shop/${slug}/customers/${order.customerId}`} className={LINK}>
            {customerName}
          </Link>
        ) : (
          customerName
        ),
    },
    { label: "Создан", value: formatDocDateTime(order.createdAt), mono: true },
    {
      label: "Оплата",
      value: slug ? (
        <Link to={orderTabHref(ctx, "payments")} className={LINK}>
          {payment?.text ?? "нет данных"}
        </Link>
      ) : (
        (payment?.text ?? "нет данных")
      ),
      tone:
        payment?.tone === "success"
          ? "success"
          : payment?.tone === "warning" || payment?.tone === "danger"
            ? "warning"
            : "default",
    },
    {
      label: "Сумма",
      value: `${formatMoney(order.totalGross)} ${currencySymbol(order.currency)} · ${order.itemsCount} поз.`,
      mono: true,
    },
    { label: "Источник", value: ORDER_SOURCE_LABEL[order.source] },
    {
      label: "Доставка",
      value:
        delivery && slug ? (
          <Link to={orderTabHref(ctx, "shipments")} className={LINK}>
            {delivery.text}
          </Link>
        ) : (
          (delivery?.text ?? "не указана")
        ),
    },
    { label: "Ответственный", value: order.ownerDisplayName ?? "не назначен" },
    {
      label: "Изменён",
      value: order.lastChangeAt ? formatDocDateTime(order.lastChangeAt) : "не менялся",
      mono: !!order.lastChangeAt,
    },
  ];
}
